import React, { useCallback, useEffect, useState } from "react";
import Button from "../../UI/Button";
import PackagingList from "./PackagingList";
import PackagingFormModal from "./PackagingFormModal";
import {
  fetchPackagings,
  createPackaging,
  updatePackaging,
  deletePackaging,
} from "./requests";

const emptyPackaging = {
  name: "",
  length: "",
  width: "",
  height: "",
  quantity: "",
  net_weight: "",
  gross_weight: "",
  eco_fee_class: 0,
  comment: "",
};

export default function ComponentCard({ component, onEdit, onDelete }) {
  const [packagings, setPackagings] = useState(component.packagings || []);
  const [expanded, setExpanded] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setPackagings(await fetchPackagings(component.id));
    } catch (e) {
      console.error(e);
    }
  }, [component.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing({ ...emptyPackaging });
    setFormOpen(true);
  };

  const openEdit = (pkg) => {
    setEditing({ ...pkg });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        name: editing.name || "",
        length: editing.length,
        width: editing.width,
        height: editing.height,
        quantity: Number(editing.quantity),
        net_weight: editing.net_weight,
        gross_weight: editing.gross_weight,
        eco_fee_class: Number(editing.eco_fee_class || 0),
        comment: editing.comment || "",
      };
      if (editing.id) {
        await updatePackaging(component.id, editing.id, payload);
      } else {
        await createPackaging(component.id, payload);
      }
      closeForm();
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await deletePackaging(component.id, deleting.id);
      setDeleting(null);
      await load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-xl bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold">{component.name}</h3>
          <div className="text-xs text-gray-500 mt-0.5">
            Кол-во в одном продукте:{" "}
            <b>{Number(component.quantity_in_product || 0)}</b> · Упаковок:{" "}
            <b>{packagings.length}</b>
          </div>
          {component.comment && (
            <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">
              {component.comment}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          <Button
            variant="edit"
            className="text-xs px-2 py-1"
            onClick={() => onEdit(component)}
          >
            Ред.
          </Button>
          <Button
            variant="danger"
            className="text-xs px-2 py-1"
            onClick={() => onDelete(component)}
          >
            Удалить
          </Button>
          <Button
            variant="secondary"
            className="text-xs px-2 py-1"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Свернуть" : `Упаковки (${packagings.length})`}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          <div className="flex justify-end">
            <Button
              variant="primary"
              className="text-xs px-2 py-1"
              onClick={openCreate}
            >
              + Упаковка
            </Button>
          </div>
          <PackagingList
            packagings={packagings}
            onEdit={openEdit}
            onDelete={(pkg) => setDeleting(pkg)}
          />
        </div>
      )}

      {formOpen && editing && (
        <PackagingFormModal
          editing={editing}
          setEditing={setEditing}
          onSave={handleSave}
          onClose={closeForm}
          saving={saving}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">Удалить упаковку?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Упаковка «{deleting.name}» будет удалена. Это действие необратимо.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDeleting(null)}
                disabled={saving}
              >
                Отмена
              </Button>
              <Button variant="danger" onClick={confirmDelete} disabled={saving}>
                {saving ? "Удаление..." : "Удалить"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}